import { useState, useEffect, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useActivityFeed, WS_EVENTS } from '../../services/websocket';
import aiblWebSocket from '../../services/websocket';
import { Icons } from './DashboardLayout';

// Activity item icons by type
const ACTIVITY_ICONS = {
  swap: '🔄',
  transfer: '💸',
  bridge: '🌉',
  wrap: '📦',
  safe_proposed: '🔐',
  safe_confirmed: '✅',
  safe_executed: '🚀',
  plugin_enabled: '🔌',
  plugin_disabled: '⏸️',
  wallet_created: '💼',
  default: '📋',
};

// Activity item component (memoized for performance)
const ActivityItem = memo(function ActivityItem({ item, isNew }) {
  const icon = ACTIVITY_ICONS[item.type] || ACTIVITY_ICONS.default;
  const timestamp = new Date(item.timestamp || item.createdAt);
  const timeAgo = getTimeAgo(timestamp);

  return (
    <motion.div
      initial={isNew ? { opacity: 0, x: -20, scale: 0.95 } : false}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 20 }}
      className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${
        isNew ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-white/[0.02] hover:bg-white/[0.04]'
      }`}
    >
      <span className="text-lg">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">
          {item.description || item.message || item.type}
        </p>
        {item.agentName && (
          <p className="text-white/40 text-xs mt-0.5">{item.agentName}</p>
        )}
      </div>
      <span className="text-white/30 text-xs whitespace-nowrap">{timeAgo}</span>
    </motion.div>
  );
});

// Main Activity Feed component
export default function ActivityFeed({ agentId = null, limit = 10, showHeader = true }) {
  const { token } = useAuth();
  const { activity, loading, refresh } = useActivityFeed(token, agentId);
  const [newItems, setNewItems] = useState(new Set());

  // Track new items for animation
  useEffect(() => {
    if (activity.length > 0) {
      const latestId = activity[0]?.id;
      if (latestId && !newItems.has(latestId)) {
        setNewItems(prev => new Set([...prev, latestId]));
        // Clear "new" status after animation
        setTimeout(() => {
          setNewItems(prev => {
            const next = new Set(prev);
            next.delete(latestId);
            return next;
          });
        }, 3000);
      }
    }
  }, [activity]);

  const displayedActivity = activity.slice(0, limit);

  return (
    <div className="space-y-3">
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-white font-medium">Activity Feed</h3>
            <ConnectionIndicator />
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            className="p-1.5 rounded-lg text-white/40 hover:text-white/60 hover:bg-white/[0.05] transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <motion.svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              animate={loading ? { rotate: 360 } : {}}
              transition={loading ? { duration: 1, repeat: Infinity, ease: 'linear' } : {}}
            >
              <path d="M21 12a9 9 0 11-2.636-6.364" />
              <path d="M21 3v6h-6" />
            </motion.svg>
          </button>
        </div>
      )}

      {loading && displayedActivity.length === 0 ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-14 rounded-xl bg-white/[0.02] animate-pulse" />
          ))}
        </div>
      ) : displayedActivity.length === 0 ? (
        <div className="py-8 text-center">
          <div className="w-12 h-12 rounded-xl bg-white/[0.03] flex items-center justify-center mx-auto mb-3">
            <Icons.Transactions size={20} className="text-white/30" />
          </div>
          <p className="text-white/40 text-sm">No activity yet</p>
          <p className="text-white/25 text-xs mt-1">Agent actions will appear here in real-time</p>
        </div>
      ) : (
        <AnimatePresence mode="popLayout">
          <div className="space-y-2">
            {displayedActivity.map((item) => (
              <ActivityItem
                key={item.id || item.timestamp}
                item={item}
                isNew={newItems.has(item.id)}
              />
            ))}
          </div>
        </AnimatePresence>
      )}
    </div>
  );
}

// Connection status indicator
export function ConnectionIndicator() {
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const unsubscribe = aiblWebSocket.on(WS_EVENTS.CONNECTION, (data) => {
      setConnected(data.connected);
    });

    // Check initial state
    setConnected(aiblWebSocket.connected);

    return unsubscribe;
  }, []);

  return (
    <div className="flex items-center gap-1.5" title={connected ? 'Real-time updates active' : 'Polling for updates'}>
      <motion.div
        className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-green-400' : 'bg-yellow-400'}`}
        animate={{ opacity: [1, 0.5, 1] }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      <span className="text-white/30 text-[10px] uppercase tracking-wider">
        {connected ? 'Live' : 'Polling'}
      </span>
    </div>
  );
}

// Compact activity feed for sidebar/widget
export function ActivityFeedCompact({ limit = 5 }) {
  const { token } = useAuth();
  const { activity, loading } = useActivityFeed(token);

  const displayedActivity = activity.slice(0, limit);

  if (loading && displayedActivity.length === 0) {
    return (
      <div className="space-y-1">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-8 rounded bg-white/[0.02] animate-pulse" />
        ))}
      </div>
    );
  }

  if (displayedActivity.length === 0) {
    return (
      <p className="text-white/30 text-xs text-center py-4">No recent activity</p>
    );
  }

  return (
    <div className="space-y-1">
      {displayedActivity.map((item) => {
        const icon = ACTIVITY_ICONS[item.type] || ACTIVITY_ICONS.default;
        return (
          <div key={item.id || item.timestamp} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-white/[0.02]">
            <span className="text-xs">{icon}</span>
            <span className="text-white/60 text-xs truncate flex-1">
              {item.description || item.type}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Helper function for relative time
function getTimeAgo(date) {
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}
